import { Metadata } from "@dropbox/dash-component-library";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CheckmarkLine } from "@dropbox/dig-icons/dist/mjs/assets";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { tagged } from "@mirage/service-logging";
import type { AssistTemplate } from "@mirage/shared/compose/assist-template";
import { LexicalContentEditable } from "@mirage/shared/compose/editor/LexicalContentEditable";
import { LexicalEditor } from "@mirage/shared/compose/editor/LexicalEditor";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo } from "react";
import { getItemDetails } from "../utils/getItemDetails";
import styles from "./TemplateCard.module.css";

const logger = tagged("TemplateCard");

interface TemplateCardProps {
  template: AssistTemplate;
  onClick: () => void;
  className?: string;
  isSelected?: boolean;
}

export const TemplateCard = memo(
  ({ template, onClick, isSelected }: TemplateCardProps) => {
    const account = useDropboxAccount();
    const { labelKey, getAvatarAlt, getProfilePhotoUrl } =
      getItemDetails(template);
    const avatarAlt = getAvatarAlt(account?.name?.display_name);
    const profilePhotoUrl = getProfilePhotoUrl(account?.profile_photo_url);

    return (
      <button
        className={classNames(styles.TemplateCard, {
          [styles.TemplateCardSelected]: isSelected,
        })}
        onClick={onClick}
        type="button"
      >
        <Box className={styles.TemplateCardPreview}>
          <Box className={styles.TemplateCardThumbnail}>
            {template.templateContent && (
              <Box className={styles.TemplateCardThumbnailContent}>
                <LexicalEditor
                  namespace="TemplatePreview"
                  editable={false}
                  onError={logger.error}
                  contentEditable={
                    <LexicalContentEditable contentEditable={false} thumbnail />
                  }
                  placeholder={null}
                  markdownContent={template.templateContent}
                  onChangeContent={() => {}}
                  isClickableLinks={false}
                />
              </Box>
            )}
          </Box>
          {isSelected && (
            <Box className={styles.TemplateCardCheckmark}>
              <UIIcon
                src={CheckmarkLine}
                size="medium"
                aria-label={i18n.t("assist_template_selected_label")}
              />
            </Box>
          )}
        </Box>
        <Box className={styles.TemplateCardContent}>
          <Box className={styles.TemplateCardHeader}>
            <Text className={styles.TemplateCardTitle}>
              <Truncate lines={2}>{template.name}</Truncate>
            </Text>
          </Box>
          <Metadata
            size="small"
            className={styles.TemplateCardMeta}
            withDividers="blank"
          >
            <Metadata.Item>
              <Metadata.Avatar
                hasNoOutline
                src={profilePhotoUrl}
                alt={avatarAlt}
              >
                {avatarAlt}
              </Metadata.Avatar>
            </Metadata.Item>
            <Metadata.Item>{i18n.t(labelKey)}</Metadata.Item>
          </Metadata>
        </Box>
      </button>
    );
  },
);

TemplateCard.displayName = "TemplateCard";
